// Web Push registration for the installed app. Permission handling is shared with the app
// icon badge in ./native.ts: they are the same browser permission, so there is one path.

import { api } from '../api/client';
import { badgePermission, isIOS, isStandalone, requestBadgePermission } from './native';

/** The endpoint this browser last registered, so a launch can spot a rotated subscription. */
const ENDPOINT_KEY = 'devrobot:push-endpoint';

export type PushState =
  /** No PushManager: an old browser, or Safari in a tab on iOS. */
  | 'unsupported'
  /** Push needs a secure context; the server is being served over plain http. */
  | 'insecure'
  /** iOS only delivers to an app on the Home Screen. */
  | 'needs-install'
  /** Supported, not yet asked. */
  | 'default'
  /** Blocked in the browser; nothing the app can do about it. */
  | 'denied'
  /** Allowed, but this device is not registered yet. */
  | 'granted'
  /** Allowed and registered. */
  | 'subscribed';

let registration: ServiceWorkerRegistration | undefined;

/** Called by initPwa once the service worker is registered. */
export function setPushRegistration(reg: ServiceWorkerRegistration): void {
  registration = reg;
}

export const pushSupported = (): boolean => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function pushState(): Promise<PushState> {
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'insecure';
  if (!pushSupported()) {
    // On iOS, PushManager only exists once the app has been added to the Home Screen, so
    // "not supported here" is usually "not installed yet" and is worth saying differently.
    return isIOS && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (isIOS && !isStandalone()) return 'needs-install';
  const permission = badgePermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'default';
  const existing = await currentSubscription();
  return existing ? 'subscribed' : 'granted';
}

async function reg(): Promise<ServiceWorkerRegistration | undefined> {
  if (registration) return registration;
  if (!('serviceWorker' in navigator)) return undefined;
  registration = await navigator.serviceWorker.ready.catch(() => undefined);
  return registration;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const r = await reg();
  if (!r) return null;
  return r.pushManager.getSubscription().catch(() => null);
}

/**
 * Turns notifications on for this device.
 *
 * Order matters: permission has to be granted before `subscribe`, or iOS throws. Must be
 * called from a real tap — Safari ignores a permission prompt that is not.
 */
export async function enablePush(): Promise<PushState> {
  const state = await pushState();
  if (state === 'unsupported' || state === 'needs-install' || state === 'insecure' || state === 'denied') return state;

  if (badgePermission() !== 'granted') {
    const permission = await requestBadgePermission();
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'default';
  }

  const r = await reg();
  if (!r) return 'unsupported';
  const { vapidPublicKey } = await api.get<{ vapidPublicKey: string }>('/api/v1/notifications/push-config');
  const subscription =
    (await r.pushManager.getSubscription()) ?? (await r.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(vapidPublicKey) }));

  await registerSubscription(subscription);
  return 'subscribed';
}

export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  if (subscription) await subscription.unsubscribe().catch(() => {});
  try {
    localStorage.removeItem(ENDPOINT_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Re-registers this browser's subscription when it has changed since the last launch.
 *
 * `pushsubscriptionchange` only fires on Chromium — Safari and Firefox rotate a subscription
 * without telling anyone — so without this check a device quietly stops receiving anything.
 */
export async function syncSubscription(): Promise<void> {
  if (!pushSupported() || badgePermission() !== 'granted') return;
  const subscription = await currentSubscription();
  if (!subscription) return;
  let known: string | null = null;
  try {
    known = localStorage.getItem(ENDPOINT_KEY);
  } catch {
    /* storage unavailable: re-register, which is harmless */
  }
  if (known === subscription.endpoint) return;
  await registerSubscription(subscription).catch(() => {});
}

async function registerSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  await api.post('/api/v1/notifications/devices', { endpoint: json.endpoint, keys: json.keys });
  try {
    localStorage.setItem(ENDPOINT_KEY, subscription.endpoint);
  } catch {
    /* storage unavailable: it will re-register next launch */
  }
}

/** Backed by a plain ArrayBuffer, which is what pushManager.subscribe accepts. */
export function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
